import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Ticket, 
  QrCode, 
  Smartphone, 
  BarChart, 
  Users,
  Shield,
  Zap,
  CheckCircle,
  ArrowRight,
  Briefcase,
  Music
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import Logo from '@/components/Logo';

export default function ChiSiamo() {
  const steps = [
    {
      number: "1",
      title: "Crea il Tuo Evento",
      description: "Registrati gratuitamente e crea il tuo evento in meno di 2 minuti. Inserisci nome, data, luogo e descrizione.",
      icon: Ticket,
      color: "from-purple-500 to-pink-500"
    },
    {
      number: "2",
      title: "Configura Biglietti e Zone",
      description: "Crea tutte le tipologie di biglietto che vuoi (Standard, VIP, Premium) e definisci le zone di accesso (ingresso, backstage, VIP lounge).",
      icon: QrCode,
      color: "from-blue-500 to-purple-500"
    },
    {
      number: "3",
      title: "Genera QR Code Personalizzati",
      description: "Ogni biglietto viene generato automaticamente con un QR code unico. Personalizza colori, aggiungi il tuo logo e immagini.",
      icon: Smartphone,
      color: "from-green-500 to-blue-500"
    },
    {
      number: "4",
      title: "Scansiona all'Ingresso",
      description: "Usa l'app scanner su qualsiasi smartphone. Scansiona i QR code, monitora entrate/uscite in tempo reale, controlla accessi multi-zona.",
      icon: CheckCircle,
      color: "from-pink-500 to-red-500"
    },
    {
      number: "5",
      title: "Analizza le Statistiche",
      description: "Accedi a statistiche dettagliate: presenze real-time, entrate/uscite, report PDF esportabili, database contatti per marketing.",
      icon: BarChart,
      color: "from-purple-500 to-blue-500"
    }
  ];

  const useCases = [
    {
      title: "Feste Private",
      description: "Gestisci inviti per compleanni, matrimoni, battesimi con guest list digitale e check-in elegante.",
      participants: "20-100",
      icon: Users,
      example: "Maria ha organizzato il suo matrimonio con 150 invitati usando ZonePass. Zero code all'ingresso, tutti i dati organizzati."
    },
    {
      title: "Eventi Aziendali",
      description: "Meeting, conferenze, team building con registrazione professionale e report dettagliati per HR.",
      participants: "50-500",
      icon: Briefcase,
      example: "TechCorp ha gestito una conferenza con 300 partecipanti, 5 sale e workshop paralleli senza problemi."
    },
    {
      title: "Concerti & Festival",
      description: "Biglietteria completa con prevendita, accessi VIP, controllo capacità e statistiche vendite.",
      participants: "500-10.000+",
      icon: Music,
      example: "Il Summer Festival ha venduto 5.000 biglietti online, gestito 3 ingressi e 8 zone VIP contemporaneamente."
    }
  ];

  const features = [
    {
      icon: Ticket,
      title: "Biglietteria Digitale",
      description: "Crea biglietti illimitati con prezzi personalizzati, early bird, sconti gruppi. Vendita online integrata."
    },
    {
      icon: QrCode,
      title: "QR Code Personalizzabili",
      description: "Ogni biglietto ha un QR code unico. Personalizza colori, aggiungi logo aziendale e immagini custom."
    },
    {
      icon: Smartphone,
      title: "Check-in Mobile",
      description: "Scanner app su qualsiasi smartphone. Scansiona migliaia di biglietti al minuto, anche offline."
    },
    {
      icon: Users,
      title: "Guest List & Database",
      description: "Gestisci invitati, ospiti VIP e staff. Crea database contatti per marketing eventi futuri."
    },
    {
      icon: Shield,
      title: "Multi-Zona & Sicurezza",
      description: "Definisci zone multiple (ingresso, VIP, backstage). Controlla accessi in tempo reale e previeni abusi."
    },
    {
      icon: BarChart,
      title: "Statistiche Real-Time",
      description: "Monitora presenze live, entrate/uscite, zone più affollate. Export PDF e CSV per analisi."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Chi Siamo - ZonePass | Come Funziona il Sistema di Gestione Eventi</title>
        <meta name="description" content="Scopri come funziona ZonePass: dalla creazione dell'evento alla scansione biglietti. Sistema professionale di biglietteria digitale con QR code, check-in multi-zona e statistiche. Guida completa passo-passo con esempi reali." />
        <meta name="keywords" content="come funziona zonepass, sistema gestione eventi, biglietteria digitale tutorial, QR code eventi, check-in app, gestione biglietti guida" />
        
        {/* Structured Data per AI e SEO */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Come Funziona ZonePass - Sistema Gestione Eventi",
            "description": "Guida completa al funzionamento di ZonePass: biglietteria digitale, QR code, check-in e statistiche",
            "author": {
              "@type": "Organization",
              "name": "ZonePass"
            },
            "publisher": {
              "@type": "Organization",
              "name": "ZonePass"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Header */}
        <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm border-b border-gray-200 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Logo size="md" linkTo="/" />
              <div className="flex items-center gap-4">
                <Link to="/examples" className="text-gray-700 hover:text-purple-600">
                  Esempi
                </Link>
                <Link to="/login">
                  <Button variant="outline">Accedi</Button>
                </Link>
                <Link to="/#register">
                  <Button className="bg-purple-600">Inizia Gratis</Button>
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-purple-50 via-white to-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-6">
              Come Funziona ZonePass
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-8">
              Il sistema completo per gestire eventi: dalla biglietteria digitale al check-in, 
              dalle statistiche all'export. Tutto in 5 semplici passi.
            </p>
            <Link to="/#register">
              <Button size="lg" className="bg-purple-600 hover:bg-purple-700">
                Prova Gratis - 2 Minuti Setup
                <ArrowRight className="ml-2" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Step by Step Process */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                5 Passi per Gestire il Tuo Evento
              </h2>
              <p className="text-xl text-gray-600">
                Semplice, veloce, professionale
              </p>
            </div>

            <div className="space-y-12">
              {steps.map((step, idx) => (
                <div key={idx} className="flex flex-col md:flex-row items-start gap-8">
                  {/* Number and Icon */}
                  <div className="flex-shrink-0">
                    <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center text-white text-3xl font-bold shadow-lg`}>
                      {step.number}
                    </div>
                  </div>

                  {/* Content */}
                  <Card className="flex-1 hover:shadow-xl transition-shadow">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <step.icon className="w-12 h-12 text-purple-600 flex-shrink-0" />
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900 mb-3">
                            {step.title}
                          </h3>
                          <p className="text-lg text-gray-700 leading-relaxed">
                            {step.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Detail */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Funzionalità Complete
              </h2>
              <p className="text-xl text-gray-600">
                Tutto ciò di cui hai bisogno in un'unica piattaforma
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, idx) => (
                <Card key={idx} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 rounded-lg bg-purple-100 flex items-center justify-center mb-4">
                      <feature.icon className="w-8 h-8 text-purple-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases with Examples */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Casi d'Uso Reali
              </h2>
              <p className="text-xl text-gray-600">
                Scopri come altri organizzatori usano ZonePass
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {useCases.map((useCase, idx) => (
                <Card key={idx} className="hover:shadow-xl transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-4">
                      <useCase.icon className="w-8 h-8 text-white" />
                    </div>
                    <span className="inline-block px-3 py-1 bg-purple-100 text-purple-600 rounded-full text-sm font-semibold mb-4">
                      {useCase.participants} partecipanti
                    </span>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      {useCase.title}
                    </h3>
                    <p className="text-gray-700 mb-4 leading-relaxed">
                      {useCase.description}
                    </p>
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                      <p className="text-sm text-gray-700 italic">
                        <strong>Caso reale:</strong> {useCase.example}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-purple-600 to-pink-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Pronto a Creare il Tuo Primo Evento?
            </h2>
            <p className="text-xl mb-8 text-purple-100">
              Registrazione gratuita in 2 minuti. Nessuna carta di credito richiesta.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/#register">
                <Button size="lg" className="bg-white text-purple-600 hover:bg-gray-100">
                  Inizia Gratis Ora
                  <ArrowRight className="ml-2" />
                </Button>
              </Link>
              <Link to="/examples">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  Vedi Altri Esempi
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-gray-400">
              &copy; 2025 ZonePass. Tutti i diritti riservati.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}
